import React, { useState, useEffect } from 'react';
import { DataTable } from '../../components/shared/DataTable';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { defaultVerificationTypes } from '../../data/mockData';
import { fetchInvoices, fetchPayouts, fetchCases, fetchClients, fetchProducts, createInvoice } from '../../api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Receipt,
  IndianRupee,
  Clock,
  CheckCircle2,
  AlertCircle,
  Download,
  Eye,
  Send,
  Edit,
  FileText,
  Filter,
  Plus,
  Trash2,
  Sparkles,
  Calculator,
  ShieldAlert,
  Percent,
  Check
} from 'lucide-react';
import { format } from 'date-fns';
import { useApp } from '../../context/AppContext';
import { generatePrintableInvoice } from '../../utils/printReport';

export default function InvoicePayments() {
  const { currentRole } = useApp();
  const [activeTab, setActiveTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);

  // Filter States for dynamic fetching
  const [filterClient, setFilterClient] = useState('all');
  const [filterProduct, setFilterProduct] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('verified');

  // Case Selection Grid States
  const [selectedCaseIds, setSelectedCaseIds] = useState([]);
  const [fetchedCases, setFetchedCases] = useState([]);

  // Manual Adjustments States
  const [manualCharges, setManualCharges] = useState([]);

  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      try {
        const [invRes, payRes, casesRes, clRes, prodRes] = await Promise.all([
          fetchInvoices().catch(() => []),
          fetchPayouts().catch(() => []),
          fetchCases().catch(() => []),
          fetchClients().catch(() => []),
          fetchProducts().catch(() => []),
        ]);
        if (isMounted) {
          if (Array.isArray(invRes)) setInvoices(invRes);
          if (Array.isArray(payRes)) {
            setPayouts(payRes.map(p => ({
              ...p,
              penaltiesSubtracted: p.type === 'field_executive' ? 500 : (p.type === 'vendor' ? 1000 : 0),
              netAmount: (p.amount || 0) - (p.type === 'field_executive' ? 500 : (p.type === 'vendor' ? 1000 : 0))
            })));
          }
          if (Array.isArray(clRes)) setClients(clRes);
          if (Array.isArray(prodRes)) setProducts(prodRes);
          if (Array.isArray(casesRes)) {
            const billableCases = casesRes.map(c => ({
              id: c.id,
              refNumber: c.refNumber,
              clientName: c.clientName || 'Client',
              productName: c.productName || 'Verification',
              baseRate: c.baseRate || 250,
              penalty: c.penalty || 0,
            }));
            setFetchedCases(billableCases);
          }
          if (Array.isArray(clRes)) setClients(clRes);
        }
      } catch (err) {
        console.error('Failed to load invoice/payout data', err);
      }
    }
    loadData();
    return () => { isMounted = false; };
  }, []);
  const [chargeLabel, setChargeLabel] = useState('');
  const [chargeAmount, setChargeAmount] = useState('');

  const [discountPercent, setDiscountPercent] = useState(5); // 5% discount

  // Fetch engine click simulation
  // Fetch engine click
  const handleFetchCases = async () => {
    try {
      const data = await fetchCases({ status: filterStatus });
      if (Array.isArray(data)) {
        const mapped = data.map(c => ({
          id: c.id,
          refNumber: c.refNumber,
          clientName: c.clientName || 'Client',
          productName: c.productName || 'Verification',
          baseRate: c.baseRate || 250,
          penalty: c.penalty || 0,
        }));
        setFetchedCases(mapped);
        toast.success(`Query executed! ${mapped.length} completed & verified cases loaded from database.`);
      }
    } catch (e) {
      toast.error('Failed to fetch cases');
    }
  };

  // Add line item
  const handleAddCharge = () => {
    const amt = parseFloat(chargeAmount);
    if (!chargeLabel || isNaN(amt)) {
      toast.error('Provide valid charge label and amount');
      return;
    }
    setManualCharges(prev => [...prev, { id: Date.now().toString(), label: chargeLabel, amount: amt }]);
    setChargeLabel('');
    setChargeAmount('');
    toast.success('Manual charge adjustment added to draft!');
  };

  // Delete line item
  const handleDeleteCharge = (id) => {
    setManualCharges(prev => prev.filter(c => c.id !== id));
    toast.success('Manual line item removed');
  };

  // Calculations
  const activeCasesList = fetchedCases.filter(c => selectedCaseIds.includes(c.id));
  const casesSubtotal = activeCasesList.reduce((sum, c) => sum + c.baseRate, 0);
  const manualSubtotal = manualCharges.reduce((sum, c) => sum + c.amount, 0);
  const totalSubtotal = casesSubtotal + manualSubtotal;

  const totalPenalties = activeCasesList.reduce((sum, c) => sum + c.penalty, 0);
  const discountAmount = Math.round((totalSubtotal - totalPenalties) * (discountPercent / 100));
  
  const taxableAmount = Math.max(0, totalSubtotal - totalPenalties - discountAmount);
  const gstAmount = Math.round(taxableAmount * 0.18);
  const finalPayable = taxableAmount + gstAmount;

  // Generate Invoice Action
  const handleGenerateInvoice = async () => {
    if (selectedCaseIds.length === 0) {
      toast.error('Select at least one verified case to invoice');
      return;
    }
    const targetClient = clients[0] || { name: 'Client' };
    const newInv = {
      id: `INV00${invoices.length + 1}`,
      number: `INV-2026-00${invoices.length + 1}`,
      client: targetClient,
      amount: finalPayable,
      status: 'pending',
      date: format(new Date(), 'yyyy-MM-dd'),
      dueDate: '2026-06-15',
      paidDate: null,
      cases: selectedCaseIds.length
    };
    try {
      await createInvoice({
        id: newInv.id,
        invoice_number: newInv.number,
        client_id: targetClient.id || null,
        amount: finalPayable,
        status: 'pending',
        cases_count: selectedCaseIds.length
      }).catch(err => console.warn('Could not post invoice to DB:', err));
    } catch (e) {}
    setInvoices(prev => [newInv, ...prev]);
    toast.success(`Invoice ${newInv.number} generated!`);
    setSelectedCaseIds([]);
  };

  // Payout action
  const handlePayoutApproval = (id, type) => {
    setPayouts(prev => prev.map(p => p.id === id ? { ...p, status: type === 'approve' ? 'approved' : 'rejected' } : p));
    toast.success(`Payout request ${type === 'approve' ? 'approved' : 'rejected'} successfully.`);
  };

  const invoiceColumns = [
    {
      key: 'number',
      header: 'Invoice #',
      sortable: true,
      render: (row) => <span className="font-mono text-xs font-semibold text-blue-600">{row.number}</span>,
    },
    {
      key: 'client',
      header: 'Client',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Avatar className="w-6 h-6">
            <AvatarImage src={row.client.logo} />
            <AvatarFallback>{row.client.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <span className="text-xs text-slate-700 font-medium">{row.client.name}</span>
        </div>
      ),
    },
    {
      key: 'cases',
      header: 'Cases Count',
      render: (row) => <span className="text-xs text-slate-600 font-mono">{row.cases}</span>,
    },
    {
      key: 'amount',
      header: 'Invoice Amount',
      sortable: true,
      render: (row) => <span className="text-xs font-bold text-emerald-600">₹{row.amount.toLocaleString()}</span>,
    },
    {
      key: 'date',
      header: 'Created Date',
      sortable: true,
      render: (row) => <span className="text-xs text-slate-600">{format(new Date(row.date), 'MMM dd, yyyy')}</span>,
    },
    {
      key: 'dueDate',
      header: 'Due Date',
      render: (row) => <span className="text-xs text-slate-600">{format(new Date(row.dueDate), 'MMM dd, yyyy')}</span>,
    },
    {
      key: 'status',
      header: 'Payment Status',
      render: (row) => (
        <StatusBadge
          status={row.status === 'paid' ? 'success' : row.status === 'pending' ? 'warning' : 'danger'}
          label={row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        />
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="icon"
            className="w-7 h-7 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
            title="View Invoice Details"
            onClick={() => {
              generatePrintableInvoice(row);
              toast.success(`Viewing invoice: ${row.invoiceNumber || row.id}`);
            }}
          >
            <Eye className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="w-7 h-7 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
            title="Download PDF Invoice"
            onClick={() => {
              generatePrintableInvoice(row);
              toast.success(`Downloading PDF for invoice: ${row.invoiceNumber || row.id}`);
            }}
          >
            <Download className="w-3.5 h-3.5" />
          </Button>
          {row.status !== 'paid' && (
            <Button
              variant="ghost"
              size="icon"
              className="w-7 h-7 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
              title="Send to Client"
              onClick={() => {
                setInvoices(prev => prev.map(inv => inv.id === row.id ? { ...inv, status: 'paid' } : inv));
                toast.success(`Invoice ${row.invoiceNumber || row.id} transmitted to client email!`);
              }}
            >
              <Send className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const payoutColumns = [
    {
      key: 'user',
      header: 'Payout Target',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Avatar className="w-7 h-7">
            <AvatarImage src={row.user.avatar} />
            <AvatarFallback>{row.user.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-xs font-semibold text-slate-800">{row.user.name}</p>
            <p className="text-[10px] text-slate-500 capitalize">{row.type.replace('_', ' ')}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'cases',
      header: 'Completed Cases',
      render: (row) => <span className="text-xs text-slate-600 font-mono">{row.cases || '-'}</span>,
    },
    {
      key: 'amount',
      header: 'Base Rate Payout',
      render: (row) => <span className="text-xs text-slate-600">₹{row.amount.toLocaleString()}</span>,
    },
    {
      key: 'penaltiesSubtracted',
      header: 'Penalty Subtracted',
      render: (row) => <span className="text-xs font-semibold text-rose-600">-{row.penaltiesSubtracted ? `₹${row.penaltiesSubtracted}` : '₹0'}</span>,
    },
    {
      key: 'netAmount',
      header: 'Net Payable',
      sortable: true,
      render: (row) => <span className="text-xs font-bold text-emerald-600">₹{row.netAmount.toLocaleString()}</span>,
    },
    {
      key: 'status',
      header: 'Approval Status',
      render: (row) => (
        <StatusBadge
          status={row.status === 'approved' ? 'success' : row.status === 'rejected' ? 'danger' : 'warning'}
          label={row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        />
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => row.status === 'pending' ? (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <Button 
            onClick={() => handlePayoutApproval(row.id, 'approve')} 
            className="bg-emerald-600 hover:bg-emerald-500 text-white h-7 text-[10px] px-2"
          >
            Approve
          </Button>
          <Button 
            onClick={() => handlePayoutApproval(row.id, 'reject')} 
            className="bg-rose-600 hover:bg-rose-500 text-white h-7 text-[10px] px-2"
          >
            Reject
          </Button>
        </div>
      ) : (
        <span className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Processed</span>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6 text-slate-800 min-h-screen bg-slate-50">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">
          Advanced Invoices & Payouts
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          Auto-fetch verified leads, apply manual charges or discounts, configure GST, and approve payout requests.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Invoiced Amount', value: `₹${(invoices.reduce((sum, i) => sum + i.amount, 0) / 100000).toFixed(2)}L`, icon: Receipt, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-100' },
          { label: 'Received Payments', value: `₹${(invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.amount, 0) / 100000).toFixed(2)}L`, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-100' },
          { label: 'Pending Collections', value: `₹${(invoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + i.amount, 0) / 100000).toFixed(2)}L`, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-100' },
          { label: 'Total Outbound Payouts', value: `₹${payouts.reduce((sum, p) => sum + p.netAmount, 0).toLocaleString()}`, icon: IndianRupee, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-100' }
        ].map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <Card key={idx} className={`crm-card p-4 backdrop-blur-xl border ${stat.bg}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-100">
                  <Icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold font-mono text-slate-900">{stat.value}</p>
                  <p className="text-[11px] text-slate-500 font-medium mt-0.5">{stat.label}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 border border-slate-200 p-1">
          <TabsTrigger value="invoices" className="data-[state=active]:bg-white data-[state=active]:text-blue-600 text-slate-600 text-xs">
            Invoicing Engine & Logs
          </TabsTrigger>
          <TabsTrigger value="payouts" className="data-[state=active]:bg-white data-[state=active]:text-blue-600 text-slate-600 text-xs">
            Field / Vendor Payouts
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Invoicing Engine */}
        <TabsContent value="invoices" className="mt-6 space-y-6">
          
          {/* Engine Controls Block */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            
            {/* Left Box: Filters & Case Selection Grid (7 Cols) */}
            <div className="lg:col-span-7 space-y-6">
              
              {/* Query Filters */}
              <Card className="crm-card border-slate-200 bg-white p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Filter className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-semibold text-slate-800">Auto-Fetch Invoicing Filters</span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 block mb-1">Select Client</label>
                    <select
                      value={filterClient}
                      onChange={(e) => setFilterClient(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-xs text-slate-700"
                    >
                      <option value="all">All Clients</option>
                      {clients.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 block mb-1">Select Product</label>
                    <select
                      value={filterProduct}
                      onChange={(e) => setFilterProduct(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-xs text-slate-700"
                    >
                      <option value="all">All Products</option>
                      {products.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 block mb-1">Verification Type</label>
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-xs text-slate-700"
                    >
                      <option value="all">All Types</option>
                      {defaultVerificationTypes.slice(0, 5).map(vt => <option key={vt.id} value={vt.name}>{vt.name}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 block mb-1">Lead Status</label>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-xs text-slate-700"
                    >
                      <option value="verified">Verified Leads</option>
                      <option value="completed">Completed Leads</option>
                      <option value="qc_approved">QC Approved</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end mt-4">
                  <Button onClick={handleFetchCases} className="bg-blue-600 hover:bg-blue-500 text-white text-xs h-8 px-4">
                    Run Auto-Fetch Engine
                  </Button>
                </div>
              </Card>

              {/* Selection Checkbox Grid */}
              <Card className="crm-card border-slate-200 bg-white p-5">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-slate-800">Select Verified Cases Checklist</span>
                  <span className="text-xs text-slate-500 font-mono">
                    {selectedCaseIds.length}/{fetchedCases.length} Selected
                  </span>
                </div>

                <div className="space-y-2.5 max-h-[250px] overflow-y-auto pr-1 custom-scrollbar">
                  {fetchedCases.map(c => {
                    const isChecked = selectedCaseIds.includes(c.id);
                    return (
                      <div
                        key={c.id}
                        onClick={() => {
                          if (isChecked) {
                            setSelectedCaseIds(prev => prev.filter(id => id !== c.id));
                          } else {
                            setSelectedCaseIds(prev => [...prev, c.id]);
                          }
                        }}
                        className={`p-3 rounded-lg border cursor-pointer transition-all flex items-center justify-between ${
                          isChecked
                            ? 'bg-blue-50 border-blue-300'
                            : 'bg-slate-50 border-slate-200 hover:bg-slate-100/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {}} // handled by div click
                            className="rounded border-slate-200 bg-white text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <p className="text-xs font-semibold text-slate-800">{c.refNumber}</p>
                            <p className="text-[10px] text-slate-500 mt-0.5">{c.clientName} • {c.productName}</p>
                          </div>
                        </div>

                        <div className="text-right flex items-center gap-4">
                          {c.penalty > 0 && (
                            <span className="text-[10px] bg-rose-50 border border-rose-200 text-rose-600 font-semibold px-2 py-0.5 rounded">
                              Penalty: -₹{c.penalty}
                            </span>
                          )}
                          <span className="text-xs font-bold text-slate-700 font-mono">
                            ₹{c.baseRate}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-slate-200 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (selectedCaseIds.length === fetchedCases.length) {
                        setSelectedCaseIds([]);
                      } else {
                        setSelectedCaseIds(fetchedCases.map(c => c.id));
                      }
                    }}
                    className="border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50 text-xs h-8"
                  >
                    Toggle Selection
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const mockNewId = `CASE00${fetchedCases.length + 1}`;
                      setFetchedCases(prev => [
                        ...prev,
                        { id: mockNewId, refNumber: `ADV-2024-0000${fetchedCases.length + 1}`, clientName: 'HDFC Bank', productName: 'Car Loan', baseRate: 300, penalty: 0 }
                      ]);
                      setSelectedCaseIds(prev => [...prev, mockNewId]);
                      toast.success('Past verified case added to active selection!');
                    }}
                    className="border-slate-200 text-blue-600 hover:bg-blue-50 text-xs h-8"
                  >
                    Add Additional Verified Case
                  </Button>
                </div>
              </Card>

            </div>

            {/* Right Box: Adjustments Panel & Live Calculator Preview (5 Cols) */}
            <div className="lg:col-span-5">
              <Card className="crm-card border-slate-200 bg-white p-5 space-y-6 flex flex-col justify-between h-full shadow-sm">
                
                {/* Manual charges block */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                    <Calculator className="w-4 h-4 text-emerald-600 animate-pulse" />
                    <span className="text-sm font-semibold text-slate-800">Manual Adjustments & Charges</span>
                  </div>

                  {/* Manual Items scroll */}
                  <div className="space-y-2 max-h-[120px] overflow-y-auto pr-1 custom-scrollbar">
                    {manualCharges.map(charge => (
                      <div key={charge.id} className="flex justify-between items-center p-2 rounded bg-slate-50 border border-slate-200 text-xs">
                        <div>
                          <p className="text-slate-700 font-medium">{charge.label}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-emerald-600 font-mono">₹{charge.amount}</span>
                          <button onClick={() => handleDeleteCharge(charge.id)} className="text-slate-500 hover:text-rose-600 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add manual charge inline controls */}
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="text"
                      placeholder="Line item description..."
                      value={chargeLabel}
                      onChange={(e) => setChargeLabel(e.target.value)}
                      className="col-span-2 bg-white border border-slate-200 rounded px-2.5 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-300"
                    />
                    <div className="flex gap-1.5">
                      <input
                        type="number"
                        placeholder="₹ Amt"
                        value={chargeAmount}
                        onChange={(e) => setChargeAmount(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-xs text-slate-800 placeholder-slate-400 text-right focus:outline-none focus:border-slate-300"
                      />
                      <Button onClick={handleAddCharge} className="bg-emerald-600 hover:bg-emerald-500 text-white p-2 h-8">
                        <Plus className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Discount percentage input */}
                  <div className="p-3.5 rounded-lg bg-slate-50 border border-slate-200 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">Bulk Invoicing Discount</span>
                      <span className="text-xs font-mono font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">
                        {discountPercent}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="20"
                      step="1"
                      value={discountPercent}
                      onChange={(e) => setDiscountPercent(parseInt(e.target.value))}
                      className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                    />
                  </div>
                </div>

                {/* Live Invoice Preview Calculator */}
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-3">
                  <div className="flex justify-between text-xs text-slate-600">
                    <span>Base Rates Subtotal ({activeCasesList.length} cases):</span>
                    <span className="font-mono font-semibold">₹{casesSubtotal}</span>
                  </div>

                  <div className="flex justify-between text-xs text-slate-600">
                    <span>Manual Extra Charges:</span>
                    <span className="font-mono font-semibold">₹{manualSubtotal}</span>
                  </div>

                  {totalPenalties > 0 && (
                    <div className="flex justify-between text-xs text-rose-600">
                      <span className="flex items-center gap-1 font-medium">
                        <ShieldAlert className="w-3.5 h-3.5 text-rose-600" />
                        Accrued Penalty Deductions:
                      </span>
                      <span className="font-mono font-bold">-₹{totalPenalties}</span>
                    </div>
                  )}

                  {discountAmount > 0 && (
                    <div className="flex justify-between text-xs text-emerald-600">
                      <span className="font-medium">Discount Adjusted:</span>
                      <span className="font-mono font-bold">-₹{discountAmount}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-xs text-slate-600">
                    <span>GST Taxable Subtotal:</span>
                    <span className="font-mono font-bold text-slate-700">₹{taxableAmount}</span>
                  </div>

                  <div className="flex justify-between text-xs text-slate-600 pb-2 border-b border-slate-200">
                    <span>Live GST Calculator (18%):</span>
                    <span className="font-mono">₹{gstAmount}</span>
                  </div>

                  <div className="flex justify-between items-center text-sm font-bold text-slate-800 pt-1">
                    <span className="flex items-center gap-1.5 text-blue-600">
                      <Sparkles className="w-4 h-4 animate-bounce" />
                      Live Preview Total
                    </span>
                    <span className="text-lg font-mono text-emerald-600">₹{finalPayable.toLocaleString()}</span>
                  </div>
                </div>

                {/* Action Hook */}
                <Button onClick={handleGenerateInvoice} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-medium py-2.5">
                  Generate Live Invoice
                </Button>

              </Card>
            </div>

          </div>

          {/* Invoices List DataTable */}
          <Card className="crm-card border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-base font-semibold text-slate-800 mb-4">Generated Invoice Records</h3>
            <DataTable
              columns={invoiceColumns}
              data={invoices}
              searchPlaceholder="Search invoice number or client..."
              selectable={false}
            />
          </Card>

        </TabsContent>

        {/* Tab 2: Outbound Payouts */}
        <TabsContent value="payouts" className="mt-6">
          <Card className="crm-card border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-base font-semibold text-slate-800 mb-4">Outbound Payout Requests</h3>
            <DataTable
              columns={payoutColumns}
              data={payouts}
              searchPlaceholder="Search payout logs..."
              selectable={false}
            />
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}
